import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ACCESS_ROLES, AccessRole, ROLE_PERMISSION } from 'src/app/app.constants';
import {
  SchemeGroup,
  SchemeLeaf,
  SchemeValidation,
  VIRTUAL_SCHEME_GROUPS,
  WELL_SCHEME,
} from './well.scheme';

// ── Type guard ───────────────────────────────────────────────────────────────

type AnySchemeNode = SchemeLeaf | SchemeGroup;

function isLeaf(node: AnySchemeNode): node is SchemeLeaf {
  return (
    typeof node === 'object' &&
    node !== null &&
    typeof (node as SchemeLeaf).apiToGui === 'function' &&
    typeof (node as SchemeLeaf).guiToApi === 'function' &&
    typeof (node as SchemeLeaf).validation === 'object'
  );
}

// ── Internal helpers ─────────────────────────────────────────────────────────

/** Flattens a SchemeGroup into { id (dot-path), leaf } pairs. */
function flattenGroup(group: SchemeGroup): Array<{ id: string; leaf: SchemeLeaf }> {
  const result: Array<{ id: string; leaf: SchemeLeaf }> = [];
  for (const [key, node] of Object.entries(group)) {
    if (isLeaf(node)) {
      result.push({ id: key, leaf: node });
    } else {
      for (const [subKey, subNode] of Object.entries(node)) {
        if (isLeaf(subNode)) {
          result.push({ id: `${key}.${subKey}`, leaf: subNode });
        } else {
          for (const [deepKey, leaf] of Object.entries(subNode)) {
            result.push({ id: `${key}.${subKey}.${deepKey}`, leaf });
          }
        }
      }
    }
  }
  return result;
}

/** Reads a value from a nested object using a dot-separated path (e.g. 'minFillage.threshold'). */
function getNestedValue(obj: Record<string, any>, path: string): any {
  return path.split('.').reduce((o, k) => o?.[k], obj);
}

/** Writes a value into a nested object at the given dot-separated path,
 *  creating intermediate objects as needed. */
function setNestedValue(obj: Record<string, any>, path: string, value: any): void {
  const keys = path.split('.');
  const last = keys.pop()!;
  const target = keys.reduce((o, k) => {
    if (o[k] === undefined) o[k] = {};
    return o[k];
  }, obj);
  target[last] = value;
}

/** Builds the Angular validator array for a scheme leaf based on its validation config. */
function leafValidators(leaf: SchemeLeaf): ValidatorFn[] {
  const { required, min, max, minLength, maxLength, pattern } = leaf.validation;
  const validators: ValidatorFn[] = [];
  if (required) validators.push(Validators.required);
  if (min !== undefined) validators.push(Validators.min(min));
  if (max !== undefined) validators.push(Validators.max(max));
  if (minLength !== undefined) validators.push(Validators.minLength(minLength));
  if (maxLength !== undefined) validators.push(Validators.maxLength(maxLength));
  if (pattern !== undefined) validators.push(Validators.pattern(pattern));
  return validators;
}

/** Builds a single FormGroup for one item in an isArray entry, using the children validators. */
function buildArrayItemFormGroup(
  child: NonNullable<SchemeValidation['children']>,
  item: Record<string, any>,
): FormGroup {
  const controls: Record<string, FormControl> = {};
  for (const [key, def] of Object.entries(child)) {
    const cv = def.validation ?? {};
    const validators: ValidatorFn[] = [];
    if (cv.required) validators.push(Validators.required);
    if (cv.min !== undefined) validators.push(Validators.min(cv.min));
    if (cv.max !== undefined) validators.push(Validators.max(cv.max));
    if (cv.minLength !== undefined) validators.push(Validators.minLength(cv.minLength));
    if (cv.maxLength !== undefined) validators.push(Validators.maxLength(cv.maxLength));
    if (cv.pattern) validators.push(Validators.pattern(cv.pattern));
    controls[key] = new FormControl(item[key] ?? def.defaultValue ?? null, validators);
  }
  return new FormGroup(controls);
}

/** Converts a dot-path id like 'minFillage.threshold' to camelCase 'minFillageThreshold'. */
function dotPathToCamel(id: string): string {
  return id.replace(/\.([a-z])/g, (_, c: string) => c.toUpperCase());
}

/**
 * Returns the SchemeGroup for a given group name.
 * For virtual groups (whose fields live at the API root), it assembles the group
 * from the individual WELL_SCHEME entries listed in VIRTUAL_SCHEME_GROUPS.
 */
function getEffectiveGroup(groupName: string): SchemeGroup {
  // Support dot-notation paths (e.g. 'channels.digital')
  if (groupName.includes('.')) {
    const [topKey, ...rest] = groupName.split('.');
    let node: any = WELL_SCHEME[topKey];
    for (const key of rest) {
      if (!node || isLeaf(node)) return {};
      node = node[key];
    }
    return node && !isLeaf(node) ? (node as SchemeGroup) : {};
  }
  const entry = WELL_SCHEME[groupName];
  if (entry && !isLeaf(entry)) return entry;
  const virtualFields = VIRTUAL_SCHEME_GROUPS[groupName];
  if (!virtualFields) return {};
  const group: SchemeGroup = {};
  for (const key of virtualFields) {
    const field = WELL_SCHEME[key];
    if (field !== undefined) group[key] = field as SchemeLeaf | Record<string, SchemeLeaf>;
  }
  return group;
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns the SchemeLeaf for a dot-path within a group (e.g. getSchemeLeaf('vfd', 'upstrokeZones')).
 * Returns undefined if the path does not resolve to a leaf.
 */
export function getSchemeLeaf(groupName: string, path: string): SchemeLeaf | undefined {
  const group = getEffectiveGroup(groupName);
  if (!Object.keys(group).length) return undefined;
  const [first, second, third] = path.split('.');
  const node = group[first];
  if (node === undefined) return undefined;
  if (second === undefined) return isLeaf(node) ? node : undefined;
  if (isLeaf(node)) return undefined;
  const subNode = (node as Record<string, SchemeLeaf | Record<string, SchemeLeaf>>)[second];
  if (subNode === undefined) return undefined;
  if (third === undefined) return isLeaf(subNode) ? subNode : undefined;
  if (isLeaf(subNode)) return undefined;
  return (subNode as Record<string, SchemeLeaf>)[third];
}

/**
 * Rebuilds a FormArray for an isArray scheme entry from live data (already apiToGui-transformed).
 * Use this in patchFromWell to correctly handle arrays whose length may differ from the defaults.
 */
export function rebuildSchemeFormArray(
  groupName: string,
  entryId: string,
  items: any[],
): FormArray {
  const leaf = getSchemeLeaf(groupName, entryId);
  if (!leaf?.validation.isArray || !leaf.validation.children) return new FormArray<any>([]);
  return new FormArray(
    items.map((item) => buildArrayItemFormGroup(leaf.validation.children!, item)),
  );
}

/**
 * Returns a FormBuilder-compatible controls config for a scheme group, with defaults and validators.
 * Dot-notation ids (e.g. 'minFillage.threshold') produce nested FormGroups.
 */
export function schemeGroupControls(groupName: string): Record<string, any> {
  const tree: Record<string, any> = {};
  for (const { id, leaf } of flattenGroup(getEffectiveGroup(groupName))) {
    const keys = id.split('.');

    let control: FormControl | FormArray;
    if (leaf.validation.isArray && leaf.validation.children) {
      const defaults = (leaf.validation.defaultValue as any[] | undefined) ?? [];
      const formGroups = defaults.map((item) => {
        const groupControls: Record<string, FormControl> = {};
        for (const [childKey, childDef] of Object.entries(leaf.validation.children!)) {
          const cv = childDef.validation ?? {};
          const childValidators: ValidatorFn[] = [];
          if (cv.required) childValidators.push(Validators.required);
          if (cv.min !== undefined) childValidators.push(Validators.min(cv.min));
          if (cv.max !== undefined) childValidators.push(Validators.max(cv.max));
          groupControls[childKey] = new FormControl(
            item[childKey] ?? childDef.defaultValue ?? null,
            childValidators,
          );
        }
        return new FormGroup(groupControls);
      });
      control = new FormArray(formGroups) as unknown as FormControl;
    } else if (leaf.validation.isArray) {
      // Simple value array (no child schema) — create a FormArray of FormControls
      const defaults = (leaf.validation.defaultValue as any[] | undefined) ?? [];
      control = new FormArray(
        defaults.map((v) => new FormControl(v, leafValidators(leaf))),
      ) as unknown as FormControl;
    } else {
      control = new FormControl(leaf.validation.defaultValue ?? null, leafValidators(leaf));
    }

    if (keys.length === 1) {
      tree[keys[0]] = control;
    } else {
      let node = tree;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!node[keys[i]]) node[keys[i]] = {};
        node = node[keys[i]];
      }
      node[keys[keys.length - 1]] = control;
    }
  }

  function wrapGroups(node: Record<string, any>): Record<string, any> {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(node)) {
      out[k] =
        v instanceof FormControl || v instanceof FormArray ? v : new FormGroup(wrapGroups(v));
    }
    return out;
  }

  return wrapGroups(tree);
}

/** Returns Angular validators for a single scheme leaf field (used for FormArray item controls). */
export function schemeLeafValidators(groupName: string, path: string): ValidatorFn[] {
  const leaf = getSchemeLeaf(groupName, path);
  return leaf ? leafValidators(leaf) : [];
}

/** Returns a map of camelCase field key → validation config for a scheme group. */
export function schemeGroupValidation(groupName: string): Record<string, SchemeLeaf['validation']> {
  return Object.fromEntries(
    flattenGroup(getEffectiveGroup(groupName)).map(({ id, leaf }) => [
      dotPathToCamel(id),
      leaf.validation,
    ]),
  );
}

/**
 * Returns a map of camelCase field key → unit suffix for a scheme group,
 * sourced from each leaf's `fieldUnit`. Use this to replace hardcoded unit
 * text (e.g. '%', 'ft/s') in templates, e.g. `fieldUnits['minFillageThreshold']`.
 */
export function schemeGroupFieldUnits(groupName: string): Record<string, string | undefined> {
  return Object.fromEntries(
    flattenGroup(getEffectiveGroup(groupName)).map(({ id, leaf }) => [
      dotPathToCamel(id),
      leaf.fieldUnit,
    ]),
  );
}

/** Message text for a non-standard error key (e.g. a custom cross-field validator, or
 *  'pattern', whose regex can't be turned into readable text automatically). */
export type SchemeErrorOverrides = Record<string, string>;

function withUnit(value: number, unit?: string): string {
  return unit ? `${value} ${unit}` : `${value}`;
}

/**
 * Renders a single control's active errors into one human-readable message, sourced from a
 * scheme leaf's `validation` (min/max/minLength/maxLength) and `fieldUnit`. Covers the standard
 * min/max/length cases; 'required' intentionally returns null — the invalid form field's own
 * styling already conveys that, so no message is needed. Anything else (e.g. 'pattern', or a
 * custom cross-field validator) also returns null unless a matching key is supplied via
 * `overrides`, so the caller can either provide its own message for that key or fall back to a
 * hand-written `<mat-error>`. When both min and max are defined, the full range is always shown
 * — even if the control only failed one of the two bounds — so the message always tells the user
 * the whole valid range.
 */
export function schemeErrorMessage(
  errors: ValidationErrors | null | undefined,
  validation: SchemeValidation,
  unit?: string,
  overrides?: SchemeErrorOverrides,
): string | null {
  if (!errors) return null;
  if (errors['required']) return null;

  for (const key of Object.keys(overrides ?? {})) {
    if (errors[key]) return overrides![key];
  }

  const { min, max, minLength, maxLength } = validation;
  if (errors['min'] || errors['max']) {
    if (min !== undefined && max !== undefined) {
      return `Must be between ${withUnit(min, unit)} and ${withUnit(max, unit)}.`;
    }
    if (errors['min'] && min !== undefined)
      return `Enter a value greater than or equal to ${withUnit(min, unit)}.`;
    if (errors['max'] && max !== undefined)
      return `Enter a value less than or equal to ${withUnit(max, unit)}.`;
  }

  if (errors['minlength'] || errors['maxlength']) {
    if (minLength !== undefined && maxLength !== undefined) {
      return `Must be between ${minLength} and ${maxLength} characters.`;
    }
    if (errors['minlength'] && minLength !== undefined)
      return `Enter at least ${minLength} characters.`;
    if (errors['maxlength'] && maxLength !== undefined)
      return `Enter at most ${maxLength} characters.`;
  }

  return null;
}

/**
 * Applies guiToApi to every field in a scheme group.
 */
export function schemeGroupGuiToApi(
  groupName: string,
  values: Record<string, any>,
): Record<string, any> {
  const result: Record<string, any> = {};
  for (const { id, leaf } of flattenGroup(getEffectiveGroup(groupName))) {
    setNestedValue(result, id, leaf.guiToApi(getNestedValue(values, id) ?? 0));
  }
  return result;
}

/**
 * Applies apiToGui to all scheme-managed fields in a well object.
 * Non-scheme fields are passed through unchanged.
 */
export function applyApiToGui(well: Record<string, any>): Record<string, any> {
  if (!well) return well;
  const result = { ...well };
  for (const [groupName, entry] of Object.entries(WELL_SCHEME)) {
    if (isLeaf(entry)) {
      // Root-level individual field — convert at well root.
      const raw = well[groupName];
      if (raw !== undefined) result[groupName] = (entry as SchemeLeaf).apiToGui(raw);
    } else if (well[groupName]) {
      // Nested group — convert fields inside well[groupName].
      result[groupName] = { ...well[groupName] };
      for (const { id, leaf } of flattenGroup(entry as SchemeGroup)) {
        const raw = getNestedValue(well[groupName], id);
        if (raw !== undefined) setNestedValue(result[groupName], id, leaf.apiToGui(raw));
      }
    }
  }
  return result;
}

/**
 * Applies access control to a scheme-backed FormGroup based on the user's role.
 *
 * A control is disabled when the user's permission is strictly less privileged than the field
 * requires. Fields with no accessLevel require Full R/W. The 'readonly' case (disable everything)
 * is handled by form.disable() in the component — this function only manages per-field access for
 * the other roles.
 */
export function applySchemeAccessControl(
  groupName: string,
  formGroup: AbstractControl,
  role: AccessRole,
): void {
  const userPermission = ROLE_PERMISSION[role];

  for (const { id, leaf } of flattenGroup(getEffectiveGroup(groupName))) {
    const control = formGroup.get(id);
    if (!control) continue;

    const required = ROLE_PERMISSION[leaf.accessLevel ?? ACCESS_ROLES.FULL_READ_AND_WRITE];
    if (userPermission > required) {
      control.disable({ emitEvent: false });
    }
  }
}

/**
 * Applies guiToApi only to fields whose value differs from the original.
 * Returns an empty object if nothing changed.
 */
export function schemeGroupChangedGuiToApi(
  groupName: string,
  current: Record<string, any>,
  original: Record<string, any>,
): Record<string, any> {
  const result: Record<string, any> = {};
  for (const { id, leaf } of flattenGroup(getEffectiveGroup(groupName))) {
    const currentVal = getNestedValue(current, id);
    const originalVal = getNestedValue(original, id);
    const isArrayValue = leaf.validation.isArray || Array.isArray(currentVal);
    const changed = isArrayValue
      ? JSON.stringify(currentVal) !== JSON.stringify(originalVal)
      : currentVal !== originalVal;
    if (changed) {
      setNestedValue(result, id, leaf.guiToApi(currentVal ?? 0));
    }
  }
  return result;
}
